import { getFirebaseDb } from '@/services/firebaseService'
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore'
import type {
  ColumnSettings,
  ColumnSettingsResponse,
  ColumnSettingsUpdateRequest,
  ViewMode,
  ColumnConfig,
} from '@farmfree/common/types/order/columnKeys'
import { DEFAULT_COLUMN_SETTINGS } from '@farmfree/common/types/order/columnKeys'
import type { ApiResponse } from '@farmfree/common/types/apiResponse'

export function createColumnSettingsService() {
  const db = getFirebaseDb()

  // 데이터 무결성 보장: 저장된 컬럼에 누락된 필드가 있으면 기본값에서 추가
  const mergeColumnsWithDefaults = (
    saved: ColumnConfig[] | undefined,
    defaults: ColumnConfig[]
  ): ColumnConfig[] => {
    if (!saved || saved.length === 0) {
      return [...defaults]
    }

    const merged = [...saved]

    // 기본값에는 있지만 저장된 값에 없는 컬럼 추가 (새로 추가된 컬럼 자동 반영)
    for (const defaultCol of defaults) {
      if (!merged.find((col) => col.key === defaultCol.key)) {
        merged.push({ ...defaultCol })
        console.log(`✨ [ColumnSettings] 새 컬럼 추가: ${defaultCol.key} (${defaultCol.label})`)
      }
    }

    return merged
  }

  // 팩토리 패턴용 API
  const getModified = async (
    companyId: string,
    since: number
  ): Promise<ApiResponse<ColumnSettings[]>> => {
    try {
      console.log('🔍 [ColumnSettings] getModified 호출:', { companyId, since })
      const docRef = doc(db, 'v2_companies', companyId, 'ui_settings', 'column_settings')
      const docSnapshot = await getDoc(docRef)
      console.log('📄 [ColumnSettings] 문서 존재 여부:', docSnapshot.exists())

      if (!docSnapshot.exists()) {
        // 문서가 없으면 기본값으로 생성 (DEFAULT_COLUMN_SETTINGS에 이미 id가 포함됨)
        const defaultSettings: ColumnSettings = DEFAULT_COLUMN_SETTINGS
        console.log('🎯 [ColumnSettings] 기본값 반환')
        return {
          isSuccess: true,
          data: [defaultSettings],
        }
      }

      const data = docSnapshot.data()
      console.log('💾 [ColumnSettings] Firestore 데이터:', data)

      const settings: ColumnSettings = {
        id: 'column_settings',
        view: {
          columns: mergeColumnsWithDefaults(
            data.view?.columns,
            DEFAULT_COLUMN_SETTINGS.view.columns
          ),
        },
        payment: {
          columns: mergeColumnsWithDefaults(
            data.payment?.columns,
            DEFAULT_COLUMN_SETTINGS.payment.columns
          ),
        },
        tracking: {
          columns: mergeColumnsWithDefaults(
            data.tracking?.columns,
            DEFAULT_COLUMN_SETTINGS.tracking.columns
          ),
        },
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
      }

      console.log('✅ [ColumnSettings] 최종 설정:', settings)

      // 컬럼 설정은 단일 문서이므로 since 값과 관계없이 항상 반환
      return {
        isSuccess: true,
        data: [settings], // 단일 설정을 배열로 감싸서 반환
      }
    } catch (error: any) {
      return {
        isSuccess: false,
        message: `컬럼 설정 조회 실패: ${error.message}`,
        data: [],
      }
    }
  }

  const getDeleted = async (companyId: string): Promise<ApiResponse<string[]>> => {
    // 컬럼 설정은 삭제되지 않으므로 빈 배열 반환
    return {
      isSuccess: true,
      data: [],
    }
  }

  const saveItem = async (
    companyId: string,
    settings: ColumnSettings
  ): Promise<ApiResponse<{ id: string }>> => {
    try {
      const docRef = doc(db, 'v2_companies', companyId, 'ui_settings', 'column_settings')

      const saveData = {
        id: 'column_settings', // id 필드 추가
        view: settings.view,
        payment: settings.payment,
        tracking: settings.tracking,
        lastUpdated: Timestamp.fromDate(new Date()),
      }

      await setDoc(docRef, saveData, { merge: true })

      return {
        isSuccess: true,
        data: { id: 'column_settings' },
      }
    } catch (error: any) {
      return {
        isSuccess: false,
        message: `컬럼 설정 저장 실패: ${error.message}`,
      }
    }
  }

  // 기존 API (하위 호환성)
  const getColumnSettings = async (companyId: string): Promise<ColumnSettingsResponse> => {
    try {
      const docRef = doc(db, 'v2_companies', companyId, 'ui_settings', 'column_settings')
      const docSnapshot = await getDoc(docRef)

      if (!docSnapshot.exists()) {
        // 설정이 없으면 기본값 반환
        return {
          isSuccess: true,
          data: DEFAULT_COLUMN_SETTINGS,
        }
      }

      const data = docSnapshot.data()
      const columnSettings: ColumnSettings = {
        id: 'column_settings',
        view: {
          columns: mergeColumnsWithDefaults(
            data.view?.columns,
            DEFAULT_COLUMN_SETTINGS.view.columns
          ),
        },
        payment: {
          columns: mergeColumnsWithDefaults(
            data.payment?.columns,
            DEFAULT_COLUMN_SETTINGS.payment.columns
          ),
        },
        tracking: {
          columns: mergeColumnsWithDefaults(
            data.tracking?.columns,
            DEFAULT_COLUMN_SETTINGS.tracking.columns
          ),
        },
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
      }

      return {
        isSuccess: true,
        data: columnSettings,
      }
    } catch (error: any) {
      console.error('컬럼 설정 조회 실패:', error)
      return {
        isSuccess: false,
        message: `컬럼 설정 조회 실패: ${error.message}`,
        data: DEFAULT_COLUMN_SETTINGS, // 오류 시에도 기본값 반환
      }
    }
  }

  // 컬럼 설정 저장
  const saveColumnSettings = async (
    companyId: string,
    request: ColumnSettingsUpdateRequest
  ): Promise<ColumnSettingsResponse> => {
    try {
      const docRef = doc(db, 'v2_companies', companyId, 'ui_settings', 'column_settings')

      // 기존 설정 조회
      const existingDoc = await getDoc(docRef)
      const existingData = existingDoc.exists() ? existingDoc.data() : {}

      // 업데이트할 데이터 준비
      const updateData = {
        ...existingData,
        [request.mode]: {
          columns: request.columns,
        },
        lastUpdated: Timestamp.fromDate(new Date()),
      }

      await setDoc(docRef, updateData, { merge: true })

      console.log(`${request.mode} 모드 컬럼 설정 저장 완료:`, request.columns.length, '개 컬럼')

      return {
        isSuccess: true,
        message: '컬럼 설정이 저장되었습니다.',
      }
    } catch (error: any) {
      console.error('컬럼 설정 저장 실패:', error)
      return {
        isSuccess: false,
        message: `컬럼 설정 저장 실패: ${error.message}`,
      }
    }
  }

  // 특정 모드의 컬럼 설정만 업데이트
  const updateModeColumns = async (
    companyId: string,
    mode: ViewMode | 'tracking',
    columns: any[]
  ): Promise<ColumnSettingsResponse> => {
    return await saveColumnSettings(companyId, { mode, columns })
  }

  // 기본값으로 리셋
  const resetToDefault = async (
    companyId: string,
    mode?: ViewMode | 'tracking'
  ): Promise<ColumnSettingsResponse> => {
    try {
      const docRef = doc(db, 'v2_companies', companyId, 'ui_settings', 'column_settings')

      if (mode) {
        // 특정 모드만 리셋
        const request: ColumnSettingsUpdateRequest = {
          mode,
          columns: DEFAULT_COLUMN_SETTINGS[mode].columns,
        }
        return await saveColumnSettings(companyId, request)
      } else {
        // 전체 리셋
        const resetData = {
          view: DEFAULT_COLUMN_SETTINGS.view,
          payment: DEFAULT_COLUMN_SETTINGS.payment,
          tracking: DEFAULT_COLUMN_SETTINGS.tracking,
          lastUpdated: Timestamp.fromDate(new Date()),
        }

        await setDoc(docRef, resetData)

        console.log('모든 컬럼 설정을 기본값으로 리셋 완료')

        return {
          isSuccess: true,
          message: '모든 컬럼 설정이 기본값으로 복원되었습니다.',
        }
      }
    } catch (error: any) {
      console.error('기본값 리셋 실패:', error)
      return {
        isSuccess: false,
        message: `기본값 리셋 실패: ${error.message}`,
      }
    }
  }

  return {
    // 팩토리 패턴용 API
    getModified,
    getDeleted,
    saveItem,

    // 기존 API (하위 호환성)
    getColumnSettings,
    saveColumnSettings,
    updateModeColumns,
    resetToDefault,
  }
}
